import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-final-form'
import cx from 'classnames'

import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import MenuButton, { MenuItem } from 'common/components/menuButton'
import { Add, Clipboard, CloseSmall } from 'common/icons'

import PasteHtmlModal from './PasteHtmlModal'

import menuButtonStyles from 'common/components/menuButton/MenuButton.module.css'

type GenerateBriefFromHtmlOptions = {
  brandVoiceId?: string
  mergeTagIds?: string[]
}

type Props = {
  fieldName: string
  isDisabled?: boolean
  isGenerating: boolean
  generatedValue?: string
  pastedHtml?: string
  onGenerateBriefFromHtml: (
    html: string,
    options?: GenerateBriefFromHtmlOptions
  ) => void
  onRemovePastedHtml: () => void
}

const PasteHtmlActions = ({
  fieldName,
  isDisabled,
  isGenerating,
  generatedValue,
  pastedHtml,
  onGenerateBriefFromHtml,
  onRemovePastedHtml,
}: Props) => {
  const form = useForm()
  const hasPastedSource = !!pastedHtml

  const [isPasteModalOpen, setIsPasteModalOpen] = useState(false)
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false)
  const isAwaitingResultRef = useRef(false)
  const previousGeneratedValueRef = useRef(generatedValue)
  const preImportValueRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (
      isAwaitingResultRef.current &&
      generatedValue &&
      generatedValue !== previousGeneratedValueRef.current
    ) {
      form.initialize((values) => ({
        ...values,
        [fieldName]: generatedValue,
      }))
      isAwaitingResultRef.current = false
    }
    previousGeneratedValueRef.current = generatedValue
  }, [generatedValue, fieldName, form])

  const handleImportHtml = (html: string) => {
    // Only capture the pre-import baseline on the *first* paste — if the
    // user replaces an already-pasted HTML without removing it first,
    // removal should still revert to the original value, not the
    // intermediate one from the previous paste.
    if (!hasPastedSource) {
      preImportValueRef.current = generatedValue
    }
    isAwaitingResultRef.current = true
    setIsPasteModalOpen(false)

    const { values } = form.getState()
    onGenerateBriefFromHtml(html, {
      brandVoiceId: (values.brand_voice as { value?: string } | undefined)
        ?.value,
      mergeTagIds: values.merge_tags as string[] | undefined,
    })
  }

  const handleRemovePastedHtml = () => {
    // form.initialize (not form.change) so this reverts to a fresh pristine
    // baseline instead of marking the field dirty — a dirty field here would
    // flip BriefPage's state machine into the old "formChangedWithBrief"
    // state, which predates (and hides) the paste-flow's Focus Themes /
    // Personalisation Tags bypass.
    form.initialize((values) => ({
      ...values,
      [fieldName]: preImportValueRef.current ?? '',
    }))
    onRemovePastedHtml()
    setIsRemoveConfirmOpen(false)
  }

  return (
    <div className="pt-2">
      <div className="flex items-center gap-2">
        <MenuButton
          menuClassName={cx(
            // @szhsin/react-menu ships its own `.szh-menu { border: none;
            // background-color: #fff }` — same specificity as these
            // Tailwind classes, so depending on CSS bundling order it can
            // win. Force these with `!important` so they always apply.
            'shadow-2xl !border !border-solid !border-gold-400 !bg-gold-40',
            menuButtonStyles.menu
          )}
          offsetY={8}
          buttonProps={{
            disabled: isDisabled || isGenerating,
            'data-testid': 'paste-html-open-menu-button',
            className:
              'flex items-center justify-center px-4 py-2 rounded-full border border-gold-400 hover:border-base-700 disabled:opacity-50 disabled:cursor-not-allowed',
            children: <Add size={4} isDefaultColor={false} />,
          }}
          options={
            <MenuItem
              hoverClassName="hover:bg-gold-300"
              data-testid="paste-html-open-modal-button"
              onClick={() => setIsPasteModalOpen(true)}
            >
              <Clipboard
                isDefaultColor={false}
                className="text-coolGray-400 mr-4"
                size={4}
              />
              Paste HTML
            </MenuItem>
          }
        />
        {hasPastedSource && (
          <>
            <div className="w-px h-4 bg-gold-400" />
            <span
              className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-maroon-40 text-maroon-500 border border-maroon-400"
              data-testid="pasted-html-chip"
            >
              <Clipboard size={4} isDefaultColor={false} />
              Pasted HTML
              <button
                type="button"
                onClick={() => setIsRemoveConfirmOpen(true)}
                disabled={isDisabled || isGenerating}
                data-testid="pasted-html-remove-button"
              >
                <CloseSmall size={3} isDefaultColor={false} />
              </button>
            </span>
          </>
        )}
      </div>

      {isGenerating && (
        <p className="text-xs text-base-500 pt-1">
          Jacquard is summarising. May take a moment...
        </p>
      )}

      <PasteHtmlModal
        isOpen={isPasteModalOpen}
        currentDescription={generatedValue}
        onClose={() => setIsPasteModalOpen(false)}
        onImport={handleImportHtml}
      />

      <ConfirmationModal
        open={isRemoveConfirmOpen}
        title="Remove pasted HTML"
        confirmationText="Removing the pasted HTML will reset the brief description to what it was before you pasted. Please note this change before proceeding."
        confirmButtonText="Proceed"
        cancelButtonText="Cancel"
        confirmButtonVariant="primary"
        onCancel={() => setIsRemoveConfirmOpen(false)}
        onConfirm={handleRemovePastedHtml}
      />
    </div>
  )
}

export default PasteHtmlActions
